import React, { useEffect, useState, useRef } from "react";
import { Truncate } from "../../Truncate/Truncate";
import InfiniteScrollTable from "../../Table/InfiniteScrollTable";
import { FetchDataArgProp, TableProps } from "../../Table/types";
import DateLabel from "../../DateLabel/DateLabel";
import BranchDisplay from "../../BranchDisplay/BranchDisplay";
import Tooltip from "../../Tooltip/Tooltip";
import { PublishStatus } from "../components/PublishStatus/PublishStatus";
import { GetEntries, OpenEntryPickerModalArgs } from "../types";
import "./InfiniteEntryTable.css";

const DEFAULT_ENVIRONMENT_COLOR = "#A9B6CB";

function useFirstMountState(): boolean {
  const isFirst = useRef(true);
  if (isFirst.current) {
    isFirst.current = false;

    return true;
  }
  return isFirst.current;
}

export interface InfiniteEntryTableProps extends
  Omit<OpenEntryPickerModalArgs, "contentTypes" | "multiple" | "max" | "onSubmit" | "getEntries"> {
  contentType: Record<string, any>;
  fetchEntries: GetEntries;
  allowMultipleSelection?: boolean;
  maxSelect?: number;
  setSelectedEntries?: (entries: Record<string, any>[]) => void;
  displayBranchInfo?: boolean;
  showOnlySelectedEntries?: boolean;
}

type TItemStatus = "loading" | "loaded";

export function InfiniteEntryTable(props: InfiniteEntryTableProps) {
  const {
    contentType,
    fetchEntries,
    allowMultipleSelection = false,
    selectedEntries = [],
    setSelectedEntries,
    maxSelect,
    users = {},
    currentUserId,
    environments = {},
    locales = {},
    currentLocale,
    displayBranchInfo,
    showOnlySelectedEntries,
    entryContentTypeMap: entriesCTsMap
  } = props;

  const [fetchStatus, setFetchStatus] = useState<{
    loading: boolean;
    itemStatusMap: Record<number, TItemStatus>;
  }>({ loading: false, itemStatusMap: {} });
  // set an initial non-zero count to show the skeleton UI for the first load
  const [data, setData] = useState<{ entries: Record<string, any>; count: number }>({ entries: {}, count: 10 })
  const isFirstMount = useFirstMountState();

  const [entryContentTypeMap, setEntryContentTypeMap] = useState<Record<string, string>>(entriesCTsMap ?? {});

  /**
   * 
   * @param data an object containing user's uid as one of its properties
   * @param userIdKey the key in the data object which has user's uid as its value 
   * @returns "Me" when the user id is equal to `currentUserId` else the user's name as provided in `users`
   */
  const getUserName = (data: Record<string, any>, userIdKey: string) => {
    let userName = ""
    const isCurrentUser = data[userIdKey] && data[userIdKey] === currentUserId
    if (isCurrentUser) {
      userName = "Me"
    } else if (data[userIdKey] && users[data[userIdKey]]) {
      userName = users[data[userIdKey]]
    }
    return userName;
  }

  // a helper function to return a publish status object as required
  // by the `PublishStatus` component. Accepts `entry.publish_details`
  const getPublishStatuses = (publishDetails: any[]) => {
    if (!publishDetails || !publishDetails.length) {
      return undefined;
    }
    return publishDetails.map((detail: any) => {
      const environment = environments[detail.environment];
      const userName = getUserName(detail, "user")
      return {
        environment: {
          name: environment?.name ?? "",
          color: environment?.color ?? DEFAULT_ENVIRONMENT_COLOR,
        },
        userName,
        time: detail.time,
        scheduledAt: detail.scheduled_at,
        locale: detail.locale ? locales[detail.locale] : "",
        version: detail.version,
      }
    })
  }

  let columns = [
    {
      Header: "Title",
      id: "title",
      default: true,
      addToColumnSelector: true,
      cssClass: "entry__title-cell",
      accessor: (data: any) => {
        return displayBranchInfo ? (
          <Tooltip content={<BranchDisplay moduleType={'entry'} moduleVal={data} titleKey={'title'} />} position="right" showArrow={false} variantType="light">
            <Truncate truncateFrom="end">{data.title[0] || 'untitled'}</Truncate>
          </Tooltip>
        ) :
          <Truncate truncateFrom="end">{data.title || 'untitled'}</Truncate>
      },
      width: 250,
    },
    {
      Header: 'Modified At',
      id: 'updated_at',
      default: true,
      addToColumnSelector: true,
      cssClass: "entry__modified-by-cell",
      accessor: (data: any): React.JSX.Element => {
        const userName = getUserName(data, "updated_by");
        return (
          <div>
            <DateLabel type="large" date={data.updated_at} />
            <span className="entry__username">
              <Truncate isResponsive truncateFrom="end">
                {userName}
              </Truncate>
            </span>

          </div>
        )
      },
      width: 250,
    },
    {
      Header: 'Publish Status',
      disableSortBy: true,
      id: 'publish_status',
      default: true,
      addToColumnSelector: true,
      accessor: (data: any): React.JSX.Element => {
        const publishStatuses = getPublishStatuses(data.publish_details);
        return (
          <PublishStatus
            publishStatuses={publishStatuses}
            currentLocale={locales[currentLocale]}
          />
        )
      },
      width: 250,
    },
    {
      Header: 'URL',
      id: 'URL',
      disableSortBy: true,
      default: true,
      addToColumnSelector: true,
      cssClass: 'entry__url',
      accessor: (data: any) => {
        if (data.url) {
          return (
            <Truncate truncateFrom='end'>
              {data.url}
            </Truncate >
          );
        }
      },
      width: 250,
    }
  ];

  if (showOnlySelectedEntries) {
    columns.push({
      Header: 'Content Type',
      id: 'content_type',
      disableSortBy: true,
      default: true,
      addToColumnSelector: true,
      cssClass: 'entry__content-type',
      accessor: (data: any) => {
        if (data.uid && entryContentTypeMap[data.uid]) {
          return (
            <Truncate truncateFrom='end'>
              {entryContentTypeMap[data.uid] ?? ""}
            </Truncate >
          );
        }
      },
      width: 250,
    })
  }

  const fetchTableData = async ({
    sortBy,
    searchText,
    limit,
  }: Partial<FetchDataArgProp>) => {
    try {
      // create loading status map and update fetch status
      const itemStatusMap = Object.fromEntries(
        Array.from({ length: limit }).map((_, index) => [
          index,
          "loading" as TItemStatus,
        ]),
      );
      setFetchStatus({ loading: true, itemStatusMap });
  
      const response = await fetchEntries({
        contentTypeUid: contentType?.uid,
        sortBy,
        searchQuery: searchText,
        limit,
      });
  
      const entries = {};
      response?.entries.forEach((entry) => {
        entry._content_type_uid = contentType.uid;
        if (entry.uid) {
          entries[entry.uid] = entry;
        }
      });
      setData({ entries, count: response.count });
  
      const itemStatusMapSuccess = Object.fromEntries(
        Array.from({ length: response.count }).map((_, index) => [
          index,
          "loaded" as TItemStatus,
        ]),
      );
      setFetchStatus({ loading: false, itemStatusMap: itemStatusMapSuccess });
    } catch (error) {
      console.error(error);
    }
  };  

  const loadMoreItems = async ({
    sortBy = null,
    skip,
    limit,
    searchText,
    startIndex,
    stopIndex,
  }: FetchDataArgProp) => {
    try {
      setFetchStatus((prevFetchStatus) => {
        const updatedItemStatusMap = { ...prevFetchStatus.itemStatusMap };
        for (let i = startIndex; i <= stopIndex; i++) {
          updatedItemStatusMap[i] = "loading";
        }
        return { loading: true, itemStatusMap: updatedItemStatusMap };
      });
  
      const response = await fetchEntries({
        contentTypeUid: contentType?.uid,
        sortBy,
        searchQuery: searchText,
        skip,
        limit,
      });
  
      const entriesToAppend: (typeof data)["entries"] = {};
  
      response?.entries?.forEach((entry) => {
        entry._content_type_uid = contentType.uid;
        if (entry.uid) {
          entriesToAppend[entry.uid] = entry;
        }
      });
  
      setData(({ entries }) => ({
        entries: { ...entries, ...entriesToAppend },
        count: response.count,
      }));
  
      const itemStatusMapSuccess = Object.fromEntries(
        Array.from({ length: response.count }).map((_, index) => [
          index,
          "loaded" as TItemStatus,
        ]),
      );
  
      setFetchStatus({ loading: false, itemStatusMap: itemStatusMapSuccess });
    } catch (error) {
      console.error("error occured in function loadMoreItems", error);
    }
  };  

  // refetch entries when content type changes
  useEffect(() => {
    if (isFirstMount) {
      return;
    }
    (async function () {
      await fetchTableData({ limit: 30 })
    })()
  }, [contentType])


  let tableProps: Partial<TableProps> = {
    // onRowClick receives a single item, set as array
    onRowClick: (entry: any) => {
      setEntryContentTypeMap((prev) => ({ ...prev, [entry.uid]: contentType.title }))
      if (setSelectedEntries) {
        setSelectedEntries([entry])
      }
    },
    singleSelectedRowId: selectedEntries.length && selectedEntries[0] ?
      selectedEntries[0].uid : undefined,
  }

  const initialSelectedRowIds = {};
  selectedEntries.forEach((entry) => {
    if (entry && entry.uid) {
      initialSelectedRowIds[entry.uid] = true;
    }
  });

  if (allowMultipleSelection) {
    tableProps = {
      isRowSelect: true,
      getSelectedRow: (rows: any) => {
        const entryContentType = {}
        const entries = rows?.map((id: string) => {
          // only selected items' ids are returned from from getSelectRow, get the entire item
          let selectedEntry = selectedEntries.find((entry) => entry?.uid === id);
          // set the content type title for the current content type entries
          if (data.entries[id] && contentType.title) {
            entryContentType[id] = contentType.title;
          }
          return selectedEntry ?? data.entries[id];
        });
        setEntryContentTypeMap((prev) => ({ ...prev, ...entryContentType }))
        if (setSelectedEntries) {
          setSelectedEntries(entries)
        }
      },
      initialSelectedRowIds,
    }
  }

  const tableData = showOnlySelectedEntries ? [...selectedEntries] : Object.values(data.entries);
  const tableCount = showOnlySelectedEntries ? tableData.length : data.count;

  return (
    <div className="infinite-entry-table">
      <InfiniteScrollTable
        name={{ singular: 'entry', plural: 'entries' }}
        uniqueKey={"uid"}
        columns={columns}
        data={tableData}
        totalCounts={tableCount}
        tableHeight={285}

        loading={fetchStatus.loading}
        itemStatusMap={fetchStatus.itemStatusMap}

        fetchTableData={fetchTableData}
        loadMoreItems={loadMoreItems}

        columnSelector={false}
        viewSelector={false}
        fullRowSelect={true}
        maxSelect={maxSelect}

        canSearch={showOnlySelectedEntries ? false : true}
        searchPlaceholder={"Search Entry"}
        v2Features={{ canFreezeCheckbox: true }}
        {...tableProps}
      />
    </div>
  )
}
